import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getVaultType,
  VAULT_TYPES,
  isBuiltInVaultType,
  isCustomVaultType,
  buildCustomVaultTypeDef,
} from '@siliconvault/shared';
import type {
  VaultTypeId,
  VaultTypeDefinition,
  CustomVaultDefinition,
  CustomVaultDefinitionInput,
} from '@siliconvault/shared';
import { apiClient } from '../api/client';
import { useAppMode } from './AppModeContext';
import { useAuthContext } from './AuthContext';

export interface Vault {
  id: string;
  userId: string;
  vaultType: VaultTypeId;
  name: string;
  slug: string | null;
  customDefinition: CustomVaultDefinition | null;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

function isVaultTypeId(s: string): s is VaultTypeId {
  return isBuiltInVaultType(s) || isCustomVaultType(s);
}

interface VaultContextValue {
  multiVaultEnabled: boolean;
  vaults: Vault[];
  isLoading: boolean;
  activeVault: Vault | null;
  activeVaultId: string | null;
  vaultTypeDef: VaultTypeDefinition | null;
  setActiveVaultId: (id: string | null) => void;
  createVault: (vaultType: VaultTypeId, name: string) => Promise<Vault>;
  createCustomVault: (name: string, definition: CustomVaultDefinitionInput) => Promise<Vault>;
  updateVaultDefinition: (vaultId: string, definition: CustomVaultDefinitionInput) => Promise<void>;
  deleteVault: (vaultId: string) => Promise<void>;
  refreshVaults: () => void;
}

const VaultContext = createContext<VaultContextValue | null>(null);

const ACTIVE_VAULT_KEY = 'siliconvault_active_vault_id';

export function VaultProvider({ children }: { children: ReactNode }) {
  const { multiVaultEnabled, isModeLoaded } = useAppMode();
  const { onLogin } = useAuthContext();
  const queryClient = useQueryClient();

  const [activeVaultId, setActiveVaultIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_VAULT_KEY);
    } catch {
      return null;
    }
  });

  // After login, the vaults query may have stale/failed data from the
  // unauthenticated session. Invalidate so it refetches with auth cookies.
  useEffect(() => {
    return onLogin(() => {
      queryClient.invalidateQueries({ queryKey: ['vaults'] });
    });
  }, [onLogin, queryClient]);

  const { data: vaultsData, isLoading, isFetched: vaultsFetched, refetch } = useQuery({
    queryKey: ['vaults'],
    queryFn: () => apiClient.listVaults(),
    staleTime: 60_000,
    enabled: multiVaultEnabled,
  });

  const vaults: Vault[] = useMemo(() => {
    if (!multiVaultEnabled) return [];
    return (vaultsData?.data ?? [])
      .filter((v) => isVaultTypeId(v.vaultType))
      .map((v) => ({
        ...v,
        vaultType: v.vaultType as VaultTypeId,
        customDefinition: v.customDefinition ?? null,
      }));
  }, [multiVaultEnabled, vaultsData]);

  // Auto-select first vault if none is active, or fix stale selection
  useEffect(() => {
    if (!multiVaultEnabled || isLoading || vaults.length === 0) return;
    const target = !activeVaultId
      ? vaults[0].id
      : vaults.some((v) => v.id === activeVaultId)
        ? null
        : vaults[0].id;
    if (target) {
      setActiveVaultIdState(target);
      try { localStorage.setItem(ACTIVE_VAULT_KEY, target); } catch {}
    }
  }, [multiVaultEnabled, isLoading, vaults, activeVaultId]);

  const setActiveVaultId = useCallback((id: string | null) => {
    setActiveVaultIdState(id);
    try {
      if (id) localStorage.setItem(ACTIVE_VAULT_KEY, id);
      else localStorage.removeItem(ACTIVE_VAULT_KEY);
    } catch {}
  }, []);

  const activeVault = vaults.find((v) => v.id === activeVaultId) ?? null;
  const vaultTypeDef = useMemo<VaultTypeDefinition | null>(() => {
    if (!activeVault) return null;
    if (isBuiltInVaultType(activeVault.vaultType)) {
      return getVaultType(activeVault.vaultType);
    }
    if (isCustomVaultType(activeVault.vaultType) && activeVault.customDefinition) {
      return buildCustomVaultTypeDef(activeVault.vaultType, activeVault.customDefinition);
    }
    return null;
  }, [activeVault]);

  const createVault = useCallback(
    async (vaultType: VaultTypeId, name: string): Promise<Vault> => {
      const raw = await apiClient.createVault(vaultType, name);
      const vault: Vault = { ...raw, vaultType: raw.vaultType as VaultTypeId, customDefinition: raw.customDefinition ?? null };
      await refetch();
      setActiveVaultId(vault.id);
      return vault;
    },
    [refetch, setActiveVaultId]
  );

  const createCustomVault = useCallback(
    async (name: string, definition: CustomVaultDefinitionInput): Promise<Vault> => {
      const raw = await apiClient.createCustomVault(name, definition);
      const vault: Vault = { ...raw, vaultType: raw.vaultType as VaultTypeId, customDefinition: raw.customDefinition ?? null };
      await refetch();
      setActiveVaultId(vault.id);
      return vault;
    },
    [refetch, setActiveVaultId]
  );

  const updateVaultDefinition = useCallback(
    async (vaultId: string, definition: CustomVaultDefinitionInput): Promise<void> => {
      await apiClient.updateVaultDefinition(vaultId, definition);
      await refetch();
    },
    [refetch]
  );

  const deleteVault = useCallback(
    async (vaultId: string): Promise<void> => {
      await apiClient.deleteVault(vaultId);
      if (activeVaultId === vaultId) {
        setActiveVaultId(null);
      }
      await refetch();
    },
    [activeVaultId, refetch, setActiveVaultId]
  );

  const noop = useCallback(() => {}, []);

  // Loading until both mode is known AND vaults have been fetched at least once
  const stillLoading = !isModeLoaded || (multiVaultEnabled && !vaultsFetched);

  const value = useMemo<VaultContextValue>(() => multiVaultEnabled
    ? {
        multiVaultEnabled,
        vaults,
        isLoading: stillLoading,
        activeVault,
        activeVaultId,
        vaultTypeDef,
        setActiveVaultId,
        createVault,
        createCustomVault,
        updateVaultDefinition,
        deleteVault,
        refreshVaults: refetch,
      }
    : {
        multiVaultEnabled: false,
        vaults: [],
        isLoading: stillLoading,
        activeVault: null,
        activeVaultId: null,
        vaultTypeDef: null,
        setActiveVaultId: noop,
        createVault: () => Promise.reject(new Error('Multi-vault not enabled')),
        createCustomVault: () => Promise.reject(new Error('Multi-vault not enabled')),
        updateVaultDefinition: () => Promise.reject(new Error('Multi-vault not enabled')),
        deleteVault: () => Promise.reject(new Error('Multi-vault not enabled')),
        refreshVaults: noop,
      },
    [multiVaultEnabled, vaults, stillLoading, activeVault, activeVaultId, vaultTypeDef, setActiveVaultId, createVault, createCustomVault, updateVaultDefinition, deleteVault, refetch, noop],
  );

  return (
    <VaultContext.Provider value={value}>
      {children}
    </VaultContext.Provider>
  );
}

export function useVault(): VaultContextValue {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVault must be used within VaultProvider');
  return ctx;
}
